// src/components/SurveyBuilder.jsx
import React, { useEffect, useState } from "react";

export default function SurveyBuilder({ initial, onChange }) {
  const [title, setTitle] = useState(initial?.title || "New ESAT Survey");
  const [elements, setElements] = useState(
    initial?.pages?.[0]?.elements || [
      { type: "text", name: "employee_name", title: "Your name", isRequired: true },
      { type: "rating", name: "satisfaction", title: "Overall satisfaction (1-5)", rateMax: 5 },
      { type: "comment", name: "comments", title: "Comments" }
    ]
  );

  useEffect(() => {
    onChange?.({ title, showQuestionNumbers: "on", pages: [{ name: "page1", elements }] });
  }, [title, elements, onChange]);

  const addQuestion = (type) => {
    const base = { type, name: `${type}_${Date.now()}`, title: `New ${type} question` };
    if (type === "dropdown" || type === "checkbox") base.choices = ["Option 1", "Option 2"];
    if (type === "rating" && base.rateMax == null) base.rateMax = 5;
    setElements((prev) => [...prev, base]);
  };
  const updateQ = (idx, patch) => setElements((prev) => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  const removeQ = (idx) => setElements((prev) => prev.filter((_, i) => i !== idx));
  const move = (idx, dir) =>
    setElements((prev) => {
      const arr = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });

  return (
    <div>
      <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-gray-100">
        Survey Title
      </label>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border bg-white text-gray-900 border-gray-300
                   focus:outline-none focus:ring-2 focus:ring-black focus:border-black
                   dark:bg-gray-900 dark:text-white dark:border-gray-700
                   dark:focus:ring-uasYellow dark:focus:border-uasYellow"
      />

      <div className="mt-4">
        <div className="text-sm font-semibold mb-2 text-gray-900 dark:text-gray-100">
          Add Question
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            ["text", "Text"],
            ["rating", "Rating"],
            ["comment", "Comment"],
            ["dropdown", "Dropdown"],
            ["checkbox", "Checkbox List"]
          ].map(([t, label]) => (
            <button
              key={t}
              onClick={() => addQuestion(t)}
              className="px-3 py-1.5 rounded-lg border text-sm font-semibold
                         bg-black text-white border-black hover:opacity-90
                         focus:outline-none focus:ring-2 focus:ring-black
                         dark:bg-uasYellow dark:text-black dark:border-transparent
                         dark:hover:brightness-95 dark:focus:ring-uasYellow"
            >
              + {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <div className="text-sm font-semibold mb-2 text-gray-900 dark:text-gray-100">
          Questions ({elements.length})
        </div>

        {elements.length === 0 ? (
          <div className="p-6 text-center rounded-lg border text-gray-600 bg-white
                          border-gray-200
                          dark:text-gray-300 dark:bg-laryzeDarkGray dark:border-gray-700">
            No questions yet. Use the buttons above to add.
          </div>
        ) : (
          elements.map((q, i) => (
            <div
              key={i}
              className="p-4 rounded-lg mb-3 border bg-white border-gray-200
                         dark:bg-laryzeDarkGray dark:border-gray-700"
            >
              <div className="flex items-center justify-between">
                <div
                  className="text-[10px] px-2 py-1 rounded font-bold tracking-wide uppercase
                             bg-black text-white
                             dark:bg-uasYellow dark:text-black"
                >
                  {q.type}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => move(i, -1)}
                    className="px-2 py-1 rounded-md text-sm border
                               bg-gray-100 hover:bg-gray-200 border-gray-300
                               focus:outline-none focus:ring-2 focus:ring-black
                               dark:bg-gray-800 dark:hover:bg-gray-700 dark:border-gray-600 dark:text-white
                               dark:focus:ring-uasYellow"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => move(i, +1)}
                    className="px-2 py-1 rounded-md text-sm border
                               bg-gray-100 hover:bg-gray-200 border-gray-300
                               focus:outline-none focus:ring-2 focus:ring-black
                               dark:bg-gray-800 dark:hover:bg-gray-700 dark:border-gray-600 dark:text-white
                               dark:focus:ring-uasYellow"
                  >
                    ↓
                  </button>
                  <button
                    onClick={() => removeQ(i)}
                    className="px-2 py-1 rounded-md text-sm font-semibold text-white
                               bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-xs block mb-1 text-gray-700 dark:text-gray-300">Name</label>
                  <input
                    value={q.name}
                    onChange={(e) => updateQ(i, { name: e.target.value })}
                    className="w-full px-3 py-2 rounded border bg-white text-gray-900 border-gray-300
                               focus:outline-none focus:ring-2 focus:ring-black focus:border-black
                               dark:bg-gray-900 dark:text-white dark:border-gray-700
                               dark:focus:ring-uasYellow dark:focus:border-uasYellow"
                  />
                </div>

                <div>
                  <label className="text-xs block mb-1 text-gray-700 dark:text-gray-300">Title</label>
                  <input
                    value={q.title || ""}
                    onChange={(e) => updateQ(i, { title: e.target.value })}
                    className="w-full px-3 py-2 rounded border bg-white text-gray-900 border-gray-300
                               focus:outline-none focus:ring-2 focus:ring-black focus:border-black
                               dark:bg-gray-900 dark:text-white dark:border-gray-700
                               dark:focus:ring-uasYellow dark:focus:border-uasYellow"
                  />
                </div>

           {(q.type === "dropdown" || q.type === "checkbox") && (
  <div className="md:col-span-2">
    <label className="text-xs block mb-2 text-gray-700 dark:text-gray-300">
      Choices
    </label>

    <div className="space-y-2">
      {(q.choices || []).map((choice, ci) => (
        <div key={ci} className="flex gap-2">
          <input
            value={choice}
            onChange={(e) => {
              const next = [...(q.choices || [])];
              next[ci] = e.target.value;
              updateQ(i, { choices: next });
            }}
            className="w-full px-3 py-2 rounded border bg-white text-gray-900 border-gray-300
                       focus:outline-none focus:ring-2 focus:ring-black focus:border-black
                       dark:bg-gray-900 dark:text-white dark:border-gray-700
                       dark:focus:ring-uasYellow dark:focus:border-uasYellow"
            placeholder={`Option ${ci + 1}`}
          />

          <button
            type="button"
            onClick={() =>
              updateQ(i, {
                choices: (q.choices || []).filter((_, k) => k !== ci),
              })
            }
            className="px-2 py-1 rounded-md text-sm border bg-gray-100 hover:bg-gray-200 border-gray-300
                       dark:bg-gray-800 dark:hover:bg-gray-700 dark:border-gray-600 dark:text-white"
            title="Remove option"
          >
            ✕
          </button>

          <button
            type="button"
            onClick={() => {
              const next = [...(q.choices || [])];
              next.splice(ci + 1, 0, `Option ${next.length + 1}`);
              updateQ(i, { choices: next });
            }}
            className="px-2 py-1 rounded-md text-sm border bg-gray-100 hover:bg-gray-200 border-gray-300
                       dark:bg-gray-800 dark:hover:bg-gray-700 dark:border-gray-600 dark:text-white"
            title="Add below"
          >
            + 
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={() =>
          updateQ(i, {
            choices: [...(q.choices || []), `Option ${(q.choices?.length || 0) + 1}`],
          })
        }
        className="mt-2 px-3 py-1.5 rounded-lg border text-sm font-semibold
                   bg-black text-white border-black hover:opacity-90
                   focus:outline-none focus:ring-2 focus:ring-black
                   dark:bg-uasYellow dark:text-black dark:border-transparent
                   dark:hover:brightness-95 dark:focus:ring-uasYellow"
      >
        + Add option
      </button>
    </div>
  </div>
)}


                {q.type === "rating" && (
                  <div>
                    <label className="text-xs block mb-1 text-gray-700 dark:text-gray-300">
                      Rate Max (default 5)
                    </label>
                    <input
                      type="number"
                      min={2}
                      max={10}
                      value={q.rateMax ?? 5}
                      onChange={(e) => updateQ(i, { rateMax: parseInt(e.target.value || 5, 10) })}
                      className="w-full px-3 py-2 rounded border bg-white text-gray-900 border-gray-300
                                 focus:outline-none focus:ring-2 focus:ring-black focus:border-black
                                 dark:bg-gray-900 dark:text-white dark:border-gray-700
                                 dark:focus:ring-uasYellow dark:focus:border-uasYellow"
                    />
                  </div>
                )}

                <div className="flex items-center gap-2 mt-2">
                  <input
                    id={`req_${i}`}
                    type="checkbox"
                    checked={!!q.isRequired}
                    onChange={(e) => updateQ(i, { isRequired: e.target.checked })}
                    className="rounded border-gray-300 text-black focus:ring-black
                               dark:text-black dark:focus:ring-uasYellow dark:border-gray-600"
                  />
                  <label htmlFor={`req_${i}`} className="text-sm text-gray-900 dark:text-gray-100">
                    Required
                  </label>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
